import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddBoxIcon from '@mui/icons-material/AddBox';
import { useGridApiRef, gridFilteredSortedRowEntriesSelector, GridEditInputCell } from '@mui/x-data-grid-pro';
import Datagrid from '../ui/Datagrid';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, CircularProgress, Dialog, DialogActions, DialogTitle, FilledInput, FormControl, Grid, IconButton, InputAdornment, InputLabel, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { SaveRounded, DeleteRounded, WarningAmberRounded, Search } from '@mui/icons-material';
import { REDUX_ACTIONS } from '../../shared/config/constants';
import { assetsGridColumnGenerator } from '../../utils/assetsGridGenerator';
import { renderCell } from '../ui/Datagrid/table_config';
import { addNewAsset, createAsset, deleteAsset, deleteNewAssetRow, getAssetsList, updateAsset } from '../../store/actions/assets';
import { listAssetTypes } from '../../store/actions/assetTypes';
import styles from './styles';
import { enqueueSnackbar } from '../../store/actions/snackbar';

const Assets = () => {
  const classes = styles();
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    data: null
  });
  const [assetColumns, setAssetColumn] = useState([]);
  const [pageSize, setPageSize] = React.useState(5);
  const assetsList = useSelector((state) => state.assets.assetsList);
  const [assetType, setAssetType] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const assetTypes = useSelector((state) => state.assetTypes);

  useEffect(() => {
    dispatch(listAssetTypes());
    return () => {
      dispatch({
        type: REDUX_ACTIONS.ASSETTYPE_RESET
      });
    };
  }, []);

  useEffect(() => {
    setExpanded(null);
    setAssetType(null);
  }, [assetTypes.data]);

  useEffect(() => {
    if (!assetType?._id) return;
    const columnsList = assetsGridColumnGenerator(
      [],
      [{ ...assetType?.details?.properties }],
      renderCell,
      renderEditAssets
    );

    dispatch({
      type: REDUX_ACTIONS.ASSETS_LIST_FIRST_PROPERTY,
      payload: columnsList?.[0]?.field || '',
    });

    const nColumnsList = [
      ...columnsList.map((column) => ({
        ...column,
        hide: column.field === '_id' || column.field === 'grid_id' || column.field === 'lastUpdate',
      })),
      {
        field: 'lastUpdate',
        headerName: 'Last Update',
        width: 240,
        headerAlign: 'center',
        align: 'center',
        hide: true,
        editable: false
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 110,
        headerAlign: 'center',
        align: 'center',
        renderCell: renderActions,
        editable: false
      },
    ];

    setAssetColumn(nColumnsList);
    if (apiRef.current?.updateColumns) {
      apiRef.current.updateColumns(nColumnsList);
    }
  }, [assetsList.data]);

  useEffect(() => {
    if (assetType?._id) {
      dispatch(getAssetsList(assetType?._id));
    }
  }, [expanded]);

  useEffect(() => {
    if (assetsList?.data?.length) {
      const firstProperty = assetsList.firstProperty;

      if (assetsList?.data[0]?.[firstProperty] === '') {
        const id = assetsList?.data[0]?._id;

        setTimeout(() => {
          if (apiRef.current.scrollToIndexes) {
            apiRef.current.scrollToIndexes({ rowIndex: 1 });
          } else {
            const rowEl = apiRef.current.getRowElement(id);
            rowEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          apiRef.current.setCellFocus(id, firstProperty);
          apiRef.current.setEditCellValue({
            id,
            field: firstProperty,
            value: ''
          });
          setTimeout(() => {
            apiRef.current.setCellFocus(id, firstProperty);
          }, 50);
        }, 50);
      }
    }
  }, [assetsList.data.length]);

  const handleChange = (panel) => (event, newExpanded) => {
    dispatch({
      type: REDUX_ACTIONS.ASSETS_LIST_SEARCH_CHANGE,
      payload: ''
    });
    if (assetsList.data?.some(item => item?.isDirty === true)) {
      dispatch(
        enqueueSnackbar(
          'Save changes to switch tabs',
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return;
    }
    setAssetType(newExpanded ? assetTypes?.data[panel] : null);
    setExpanded(newExpanded ? panel : false);
  };

  const validateRow = (row) => {
    const ignoredKeys = ['_id', 'grid_id', 'lastUpdate', 'isNewRow', 'isDirty'];
    const emptyKeys = Object.entries(row)
      .filter(([key]) => !ignoredKeys.includes(key))
      .filter(([_, value]) =>
        value === '' ||
        value === null ||
        value === undefined
      )
      .map(([key]) => key);
    if (emptyKeys.length > 0) {
      dispatch(
        enqueueSnackbar(
          `Please fill the following fields: ${emptyKeys}`,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }
    return true;
  };

  const handleSave = (row) => {
    if (validateRow(row)) {
      setTimeout(() => {
        const value = apiRef.current.getRow(row._id);
        delete value.grid_id;
        const body = {
          ...value,
          Type: assetType?.details?.name
        };
        delete body.isDirty;
        delete body?.lastUpdate;
        if (!row?.isNewRow) {
          dispatch(updateAsset(body, assetType._id));
        } else {
          delete body._id;
          delete body.isNewRow;
          dispatch(createAsset(body, assetType._id));
        }
      }, 100);
    }
  };

  const handleDelete = () => {
    if (confirmDelete?.data?.isNewRow) {
      handleDeleteAssetRow(confirmDelete?.data?._id);
      return;
    }
    dispatch(deleteAsset({ _id: confirmDelete.data._id }, assetType._id, () => {
      setConfirmDelete({
        open: false,
        data: null
      });
    }));
  };

  const renderActions = ({ row }) => {
    return (
      <Grid container justifyContent="center">
        <IconButton
          variant="solid"
          onClick={() => {
            handleSave(row);
          }}
          disabled={!row?.isDirty}
        >
          <SaveRounded />
        </IconButton>
        <IconButton
          variant="solid"
          onClick={() => {
            setConfirmDelete({
              open: true,
              data: row
            });
          }}
        >
          <DeleteRounded />
        </IconButton>
      </Grid>
    );
  };

  const getValidationType = (field) => {
    const validationType = assetType?.details?.properties[field];
    if (validationType) {
      return validationType;
    }
    return 'Alphanumeric';
  };

  const handleValidation = (type, value) => {
    switch (type) {
      case 'Alphanumeric':
        return (/^[a-z0-9 ]+$/i).test(value);

      case 'Number':
        return (/^\d+$/).test(value);

      case 'Decimal':
        return (/^(\d+\.?\d*|\.\d+)$/).test(value);

      default:
        return false;
    }
  };

  const handleAddAssetRow = () => {
    const rowValues = Object.fromEntries(
      Object.keys(assetType?.details?.properties).map(key => [key, ''])
    );
    dispatch(addNewAsset(rowValues));
  };

  const handleDeleteAssetRow = (_id) => {
    dispatch(deleteNewAssetRow(_id));
    setConfirmDelete({
      open: false,
      data: null
    });
  };

  const updateData = (id, field, value) => {
    const updatedData = assetsList.data.map((row) => {
      if (row._id === id) {
        const updatedValue = typeof value === 'string' ? value : Number(value);
        const isDirty = value !== row[field];
        return {
          ...row,
          [field]: updatedValue,
          isDirty: row.isDirty || isDirty,
        };
      }
      return row;
    });
    dispatch({
      type: REDUX_ACTIONS.ASSETS_LIST_SUCCESS,
      payload: {
        data: updatedData,
        paginatedElements: updatedData?.length,
      },
    });
  };

  const handleSearch = () => {
    dispatch({
      type: REDUX_ACTIONS.ASSETS_LIST_FILTER,
    });
  };

  const handleEnter = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const renderEditAssets = (params) => {
    const { id, field } = params;
    const validationType = getValidationType(field);
    return (
      <GridEditInputCell
        {...params}
        onChange={async (event) => {
          const newValue = event.target.value;

          if (!newValue || handleValidation(validationType, newValue)) {
            const value = getValidationType(field) === 'Number' ? Number(newValue) : newValue;
            await apiRef.current.setEditCellValue({ id, field, value });
            updateData(id, field, value);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Tab') {
            e.preventDefault();
            // manually commit previous, set next cell edit mode
          }
        }}
      />
    );
  };

  return (
    <Grid container justifyContent="center">
      {!assetTypes?.loading
        ? (
          <>
            {!assetTypes?.data?.length
              ? (
                <Grid item xs={12}>
                  <Typography textAlign='center' variant='h5' mt={20}>No Asset Types found</Typography>
                </Grid>
                )
              : (
                <Grid item xs={12}>
                  {assetTypes?.data?.map((item, key) => {
                    return (
                      <Accordion
                        id={`back-to-${key}`}
                        key={key}
                        expanded={expanded === key}
                        onChange={handleChange(key)}
                        sx={{
                          '&.Mui-expanded': {
                            marginBottom: '10px',
                            paddingTop: '5px'
                          },
                          borderTop: '1px solid #CCC',
                          padding: '10px',
                          marginBottom: '10px'
                        }}
                        TransitionProps={{
                          timeout: expanded === key ? 500 : 0
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls="panel1-content"
                          id="panel1-header"
                          sx={{
                            fontSize: '20px',
                            fontWeight: 'bold',
                          }}
                        >
                          <Box mt={expanded === key && 2}>
                            {item?.details?.name}
                          </Box>
                          {expanded === key && (
                            <Box
                              sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}
                              onClick={(e) => e.stopPropagation()}
                              onFocus={(e) => e.stopPropagation()}
                            >
                              <FormControl disabled={assetsList.data?.some(item => item?.isDirty === true)} fullWidth style={{ marginRight: '5px' }}>
                                <InputLabel
                                  id="search-label"
                                  inputprops={{ classes: { root: classes.searchBar } }}
                                >
                                  Search
                                </InputLabel>
                                <FilledInput
                                  className={classes.searchBar}
                                  id="search-input"
                                  type="text"
                                  label="Search"
                                  size="small"
                                  value={assetsList?.searchTerm}
                                  onChange={(e) => {
                                    dispatch({
                                      type: REDUX_ACTIONS.ASSETS_LIST_SEARCH_CHANGE,
                                      payload: e.target.value
                                    });
                                  }}
                                  onKeyDown={handleEnter}
                                  endAdornment={
                                    <InputAdornment position="end">
                                      <IconButton
                                        edge="end"
                                        onClick={() => handleSearch()}
                                      >
                                        <Search />
                                      </IconButton>
                                    </InputAdornment>
                                  }
                                />
                              </FormControl>
                              <IconButton disabled={assetsList.data?.some(item => item?.isDirty === true)} onClick={handleAddAssetRow}>
                                <AddBoxIcon fontSize='large' />
                              </IconButton>
                            </Box>
                          )}
                        </AccordionSummary>
                        {expanded === key && (
                          <AccordionDetails>
                            <Grid item xs={12}>
                              <Datagrid
                                apiRef={apiRef}
                                data={assetsList?.data}
                                getRowId={(row) => row._id}
                                columns={assetColumns}
                                loading={assetsList.loading}
                                toolbar={{
                                  options: {
                                    columns: true,
                                    filters: true,
                                    export: false,
                                  },
                                }}
                                pageSize={pageSize}
                                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                                rowsPerPageOptions={[5, 10, 20, 50, 100]}
                                pagination
                                height={'auto'}
                                editMode="cell"
                                sx={{
                                  '.MuiDataGrid-cell': {
                                    fontWeight: 'bold',
                                    fontSize: '16px'
                                  },
                                  '.MuiDataGrid-columnHeaderTitle': {
                                    fontWeight: 'bold',
                                  },
                                }}
                                onFilterModelChange={() => {
                                  setTimeout(() => {
                                    const filterData = gridFilteredSortedRowEntriesSelector(apiRef.current.state, apiRef.current.instanceId).map(item => {
                                      return { ...item.model };
                                    });

                                    if (filterData) {
                                      dispatch({
                                        type: REDUX_ACTIONS.ASSETS_LIST_FILTER,
                                        payload: {
                                          data: filterData,
                                        }
                                      });
                                    }
                                  }, 300);
                                }}
                              />
                            </Grid>
                          </AccordionDetails>
                        )}
                      </Accordion>
                    );
                  })}
                </Grid>
                )}
          </>
          )
        : (
          <Box mt={30}>
            <CircularProgress />
          </Box>
          )}
      {confirmDelete.open
        ? (
          <Dialog
            open={confirmDelete.open}
            onClose={() => {
              setConfirmDelete({
                open: false,
                data: null
              });
            }}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            maxWidth='md'
          >
            <Box
              sx={{ display: 'flex', padding: '20px' }}
            >
              <DialogTitle id="alert-dialog-title" display='flex' alignItems='center' fontSize='20px' fontWeight='bold'>
                <WarningAmberRounded fontSize='large' sx={{ marginBottom: '4px', marginRight: '4px' }} />
                Delete Asset?
              </DialogTitle>
              <DialogActions>
                <Button
                  style={{ backgroundColor: '#444' }}
                  onClick={() => {
                    setConfirmDelete({
                      open: false,
                      data: null
                    });
                  }}>Cancel</Button>
                <Button
                  style={{ backgroundColor: '#444' }}
                  onClick={() => {
                    handleDelete();
                  }} autoFocus>
                  Confirm
                </Button>
              </DialogActions>
            </Box>
          </Dialog>
          )
        : null}
    </Grid>
  );
};

Assets.propTypes = {
  row: PropTypes.object,
  value: PropTypes.object
};

export default Assets;
